import { useCallback, useState } from 'react';

import adminAPI from '@/api/admin';
import { ErrorAlert, SuccessAlert } from '@/components/Alert';
import FileUploadButtonComponent from '@/components/FileUploadButton';
import InputInline from '@/components/InputInline';
import Layout from '@/components/Layout';
import LoadingAnimation from '@/components/LoadingAnimation';
import Modal from '@/components/Modals/Modal';
import OrganisationsListItem from '@/components/OrganisationsListItem';
import Pagination from '@/components/Pagination';
import Search from '@/components/Search';
import Sort from '@/components/Sort';
import MESSAGES from '@/constants/messages-en';
import { capitalize, classNames } from '@/helpers/strings';
import IOrganisation from '@/types/IOrganisation';

export default function OrganisationsListPage() {
  const [data, setData] = useState<IOrganisation[]>();
  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [sort, setSort] = useState('date');
  const [order, setOrder] = useState('desc');
  const [search, setSearch] = useState('');
  const [success, setSuccess] = useState<string>('');
  const [setupOrganisationsError, setSetupOrganisationsError] =
    useState<string>('');

  const [isSetupModalOpen, setIsSetupModalOpen] = useState(false);
  const [setupSettingsLevel, setSetupSettingsLevel] = useState<
    'organisation' | 'profile'
  >('organisation');
  const [setupCSVfile, setSetupCSVfile] = useState<File>();

  const settingLevels = ['organisation', 'profile'].map(level => ({
    title: capitalize(level),
    value: level,
  }));

  async function listOrganisations(
    newPage: number = page,
    newPageSize: number = pageSize,
    newSort: string = sort,
    newOrder: string = order,
    newSearch: string = search,
  ) {
    const res = await adminAPI.listOrganisations({
      page: newPage,
      pageSize: newPageSize,
      sort: newSort,
      order: newOrder,
      search: newSearch,
    });

    setData(res.data.data);
    setPage(newPage);
    setPageSize(newPageSize);

    return res.data;
  }

  const handleSetupOrganisations = useCallback(() => {
    if (setupCSVfile) {
      setSetupOrganisationsError('');
      setSuccess('');
      setIsLoading(true);
      const formData = new FormData();
      formData.append('file', setupCSVfile);
      formData.append('settings_level', setupSettingsLevel);
      adminAPI
        .setupOrganisations(formData)
        .then(response => {
          setIsSetupModalOpen(false);
          setSuccess(MESSAGES.organisation.setup.success);
          listOrganisations();
        })
        .catch(error =>
          setSetupOrganisationsError(MESSAGES.organisation.setup.error),
        )
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [setupCSVfile, listOrganisations]);

  return (
    <Layout pageName="Organisations">
      <div className="md:pb-8 md:flex md:items-center md:justify-end md:-mt-20">
        <div className="mt-3 flex space-x-4 md:mt-0 md:ml-4">
          <Modal
            isOpen={isSetupModalOpen}
            setIsOpen={setIsSetupModalOpen}
            buttonTitle="Bulk Setup"
            dialogTitle={MESSAGES.organisation.setup.heading}
            dialogDescription={MESSAGES.organisation.setup.description}
            successButtonText={MESSAGES.organisation.setup.successButton}
            onSuccess={() => handleSetupOrganisations()}
            isLoading={isLoading}
          >
            <div className="mt-6 space-y-4">
              <div className="flex items-center">
                <span className="pr-2 text-sm font-normal leading-5 text-gray-500">
                  Don't have a CSV file?
                </span>
                &nbsp;
                <a href="/organisation_setup.csv">
                  <span className="pr-1">Dowload .csv template</span>
                </a>
              </div>
              {setupOrganisationsError && (
                <ErrorAlert message={setupOrganisationsError} />
              )}
              <div className="flex flex-row space-x-3">
                <span className="text-sm font-normal leading-5 text-gray-500">
                  Settings level:
                </span>
                &nbsp;
                <InputInline
                  type="radio"
                  data={setupSettingsLevel}
                  setData={setSetupSettingsLevel}
                  setting={settingLevels}
                  labelClassName="!font-normal text-gray-500"
                />
              </div>
              <div className="border rounded-md px-4 mt-6 flex flex-row items-center justify-between">
                <FileUploadButtonComponent
                  filename={setupCSVfile?.name}
                  fileFormat=".csv"
                  fileFormatMessage="(.csv only)"
                  onFileSelected={file => setSetupCSVfile(file)}
                  loading={isLoading}
                />
                {setupCSVfile && (
                  <button
                    type="button"
                    onClick={() => setSetupCSVfile(undefined)}
                    className="appearance-none text-brand-500 text-sm font-medium"
                  >
                    Remove link
                  </button>
                )}
              </div>
            </div>
          </Modal>
        </div>
      </div>
      <div className="pb-8 pt-8 md:pt-0">
        {success !== '' && (
          <div className="mb-5">
            <SuccessAlert message={success} />
          </div>
        )}
        <div className="pb-8 flex flex-col space-y-4 items-start sm:flex-row sm:space-y-0 sm:items-center sm:justify-end">
          <div className="flex flex-shrink-0 space-x-4">
            <Search
              id={`OrganisationsList-${page}-${pageSize}-${sort}-${order}-${search}`}
              search={search}
              setSearch={setSearch}
              fetchQuery={searchQuery =>
                listOrganisations(page, pageSize, sort, order, searchQuery)
              }
            />
            <Sort
              id={`OrganisationsList-${page}-${pageSize}-${sort}-${order}-${search}`}
              options={[
                {
                  sort: 'date',
                  order: 'desc',
                  label: 'Date created (Newest first)',
                },
                {
                  sort: 'date',
                  order: 'asc',
                  label: 'Date created (Oldest first)',
                },
                {
                  sort: 'last_login',
                  order: 'desc',
                  label: 'Last login (Newest first)',
                },
                {
                  sort: 'last_login',
                  order: 'asc',
                  label: 'Last login (Oldest first)',
                },
              ]}
              sort={sort}
              setSort={setSort}
              order={order}
              setOrder={setOrder}
              fetchQuery={(sortQuery, orderQuery) =>
                listOrganisations(page, pageSize, sortQuery, orderQuery, search)
              }
            />
          </div>
        </div>
        {data && data.length > 0 ? (
          <div className="flex flex-col">
            <div className="relative -mx-4 sm:-mx-6 md:mx-0 lg:-mx-8">
              <div className="block w-full lg:px-8">
                <div className="relative shadow-sm border border-gray-200 sm:rounded-md min-h-8 overflow-hidden">
                  <ul
                    className={classNames(
                      isLoading ? 'opacity-40' : '',
                      'divide-y divide-gray-200',
                    )}
                  >
                    <li
                      key="OrganisationsListHeader"
                      className="hidden lg:block"
                    >
                      <div className="bg-gray-50">
                        <div className="flex items-center px-4 py-2 sm:px-6">
                          <div className="min-w-0 flex-1 flex items-start md:items-center">
                            <div className="min-w-0 flex-1 md:grid md:grid-cols-4 md:gap-4 xl:grid-cols-8">
                              <p className="col-span-1 text-sm font-medium text-gray-900">
                                ORG ID
                              </p>
                              <p className="md:mt-0 col-span-1 text-sm font-medium text-gray-900">
                                ORG NAME
                              </p>
                              <p className="md:mt-0 col-span-1 text-sm font-medium text-gray-900 hidden xl:block">
                                ACCOUNTS
                              </p>
                              <p className="md:mt-0 col-span-1 text-sm font-medium text-gray-900 hidden xl:block">
                                PROFILES
                              </p>
                              <p className="md:mt-0 col-span-1 text-sm font-medium text-gray-900 hidden xl:block">
                                CREATED
                              </p>
                              <p className="md:mt-0 col-span-1 text-sm font-medium text-gray-900 hidden xl:block">
                                LAST LOGIN
                              </p>
                              <p className="col-span-1 md:col-span-2" />
                            </div>
                          </div>
                        </div>
                      </div>
                    </li>
                    {data?.map(item => (
                      <OrganisationsListItem
                        key={item.id}
                        id={item.id}
                        name={item.name}
                        email={item.email}
                        insertedAt={new Date(item.inserted_at)}
                        noOfAccounts={item.no_of_accounts}
                        noOfActiveProfiles={item.no_of_active_profiles}
                        noOfEditingProfiles={item.no_of_editing_profiles}
                        noOfProfiles={item.no_of_profiles}
                        noOfUnprintedProfiles={item.no_of_unprinted_profiles}
                        viewOrganisation={`/view-organisation/${item.id}`}
                        lastLogin={
                          item.last_login ? new Date(item.last_login) : null
                        }
                      />
                    ))}
                  </ul>
                  {isLoading && (
                    <div className="absolute text-gray-500 left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2">
                      <LoadingAnimation className="w-16 h-16 mx-auto text-brand-500" />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="py-32">
            <h3 className="w-full text-center text-2xl leading-8 text-gray-900 font-medium">
              {MESSAGES.organisation.list.empty.heading}
            </h3>
            <p className="w-full text-center mt-2 text-sm leading-5 text-gray-500">
              {MESSAGES.organisation.list.empty.description}
            </p>
          </div>
        )}
      </div>
      <Pagination
        id={`OrganisationsList-${page}-${pageSize}-${sort}-${order}-${search}`}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        fetchQuery={(pageQuery, pageSizeQuery) =>
          listOrganisations(pageQuery, pageSizeQuery, sort, order, search)
        }
        setIsLoading={setIsLoading}
      />
    </Layout>
  );
}
